import { adapter } from '@seek/tealium-adapter';

import {
  useGetApproachability,
  useGetProfileVisibility,
  useGetSharedProfileVisibility,
} from '../graphql/visibility/data';

import { allTrackingState, type InitialAppState } from './analytics';

const tealium = adapter();

const CURRENT_PAGE_SECTION = ['employer site', 'shared profile'] as const;

export type AnalyticsCurrentPageSection = (typeof CURRENT_PAGE_SECTION)[number];

const PROFILE_VISIBILITY_LEVEL = [
  'standard',
  'limited',
  'hidden',
  'not set',
] as const;
export type AnalyticsProfileVisibilityLevel =
  (typeof PROFILE_VISIBILITY_LEVEL)[number];

const PROFILE_APPROACHABILITY_STATUS = ['on', 'off'] as const;
export type AnalyticsProfileApproachabilityStatus =
  (typeof PROFILE_APPROACHABILITY_STATUS)[number];

const SHARED_PROFILE_STATUS = ['on', 'off', 'not generated'] as const;
export type AnalyticsSharedProfileStatus =
  (typeof SHARED_PROFILE_STATUS)[number];

export const getSharedProfileTrackingStatus = ({
  isShareableProfileCreated,
  isShareableProfileActive,
}: {
  isShareableProfileCreated: boolean;
  isShareableProfileActive: boolean;
}) => {
  if (!isShareableProfileCreated) {
    return 'not generated';
  }
  if (isShareableProfileActive) {
    return 'on';
  }
  return 'off';
};

export interface ProfileTrackingState {
  profileVisibilityLevel?: AnalyticsProfileVisibilityLevel;
  profileApproachabilityStatus?: AnalyticsProfileApproachabilityStatus;
  profileSharedStatus?: AnalyticsSharedProfileStatus;
}

export let profileTrackingState: ProfileTrackingState;

export const setProfileTrackingState = (state: ProfileTrackingState): void => {
  profileTrackingState = state;
};

export const useSetProfileTrackingState = () => {
  const { profileVisibilityLevel: visibilityLevel } = useGetProfileVisibility();
  const { approachable } = useGetApproachability();
  const { isShareableProfileActive, shareableProfileUrl } =
    useGetSharedProfileVisibility();

  const profileVisibilityLevel = visibilityLevel
    ? (visibilityLevel?.toLowerCase() as AnalyticsProfileVisibilityLevel)
    : 'not set';

  const profileApproachabilityStatus = approachable ? 'on' : 'off';

  const profileSharedStatus = getSharedProfileTrackingStatus({
    isShareableProfileCreated: Boolean(shareableProfileUrl),
    isShareableProfileActive,
  });

  const profileState: ProfileTrackingState = {
    profileVisibilityLevel,
    profileApproachabilityStatus,
    profileSharedStatus,
  };

  setProfileTrackingState(profileState);
};

export const createProfileAnalytics = (initialState: InitialAppState) => {
  const trackProfileCardOpened = ({
    currentPageSubsection,
  }: {
    currentPageSubsection: AnalyticsCurrentPageSection;
  }) => {
    tealium.link('profile_section_displayed', {
      ...initialState,
      ...allTrackingState(),
      eventName: 'profile_section_displayed',
      currentPage: 'visibility settings',
      currentPageSection: 'visibility settings',
      currentPageSectionState: 'edit',
      currentPageSubsection,
    });
  };

  const trackProfileVisibilityUpdated = (
    profileVisibilityLevel: AnalyticsProfileVisibilityLevel,
  ) => {
    tealium.link('profile_update_succeeded', {
      ...initialState,
      ...allTrackingState(),
      eventName: 'profile_update_succeeded',
      currentPage: 'visibility settings',
      currentPageSection: 'visibility settings',
      updateType: 'edit',
      currentPageSubsection: 'employer site',
      profileVisibilityLevel,
    });
  };

  const trackApproachabilityUpdated = ({
    approachable,
  }: {
    approachable: boolean;
  }) => {
    tealium.link('profile_update_succeeded', {
      ...initialState,
      ...allTrackingState(),
      eventName: 'profile_update_succeeded',
      currentPage: 'visibility settings',
      currentPageSection: 'visibility settings',
      updateType: 'edit',
      currentPageSubsection: 'approachability',
      profileApproachabilityStatus: approachable ? 'on' : 'off',
    });
  };

  const trackSharedProfileVisibilityUpdated = ({
    profileSharedStatus,
  }: {
    profileSharedStatus: AnalyticsSharedProfileStatus;
  }) => {
    tealium.link('profile_update_succeeded', {
      ...initialState,
      ...allTrackingState(),
      eventName: 'profile_update_succeeded',
      currentPage: 'visibility settings',
      currentPageSection: 'visibility settings',
      updateType: 'edit',
      currentPageSubsection: 'shared profile',
      profileSharedStatus,
    });
  };

  const trackSharedProfileLinkCreated = () => {
    tealium.link('profile_update_succeeded', {
      ...initialState,
      ...allTrackingState(),
      eventName: 'profile_update_succeeded',
      currentPage: 'visibility settings',
      currentPageSection: 'visibility settings',
      currentPageSubsection: 'shared profile',
      updateType: 'add',
      profileSharedStatus: 'on',
    });
  };

  return {
    trackProfileCardOpened,
    trackProfileVisibilityUpdated,
    trackApproachabilityUpdated,
    trackSharedProfileVisibilityUpdated,
    trackSharedProfileLinkCreated,
  };
};

export type ProfileAnalytics = ReturnType<typeof createProfileAnalytics>;
